import { useEffect, useRef, useState } from 'react';
import { Loader2, ShieldCheck, AlertCircle, ExternalLink } from 'lucide-react';
import { api, API } from '../lib/api';
import { parseMsg91Mobile } from '../lib/parseMsg91Mobile';
import { classifyUserAgent, isInAppBrowser, openInChromeUrl, isIOS } from '../lib/inAppBrowser';
import Msg91ManualOtp from './Msg91ManualOtp';
import { BUILD_VERSION } from '../lib/buildVersion';

/**
 * Msg91Widget — iter-51.19.
 *
 * Loads MSG91's `otp-provider.js` Web SDK and drives the DEFAULT UI
 * (MSG91 renders its own phone-entry / OTP-entry / resend flow). On
 * a successful OTP the widget hands the browser an opaque access
 * token, which this component forwards to `onVerified(accessToken)`.
 *
 * The parent form is responsible for what happens with that token —
 * for signup we POST it to `/auth/register-with-otp` (email+password
 * flow) or `/auth/session/complete` (Google flow). Either way the
 * server MUST verify the token with MSG91's own /verifyAccessToken
 * before trusting anything. The browser is NEVER trusted to declare
 * that a mobile is verified — the server calls MSG91 to confirm.
 *
 * Iter-51.19: this component now has ONLY four visible states —
 *   'loading-config' | 'loading-sdk' | 'ready' | 'error'.
 * No 'verified' state. The "Mobile verified — creating your account…"
 * message is owned entirely by the parent page so a stale widget state
 * can never contradict the parent's error state. The parent can also
 * force a fresh widget mount by changing the `key` prop.
 *
 * Iter-51.19 also adds a hard single-fire guard: MSG91's SDK has been
 * observed on their community forums to invoke the success callback
 * twice on a rapid double-tap of the Verify button. If we forward the
 * same access token to the backend twice, MSG91's own
 * `verifyAccessToken` endpoint rejects the second call as "token
 * already used" — which surfaced as the exact production symptom the
 * user reported. `firedRef` guarantees `onVerified` runs at most once.
 *
 * Props:
 *   • onVerified(accessToken) — required. Fires ONCE, on MSG91 success.
 *   • onError(message) — optional. Fires on widget load failure or
 *                        MSG91 explicit failure.
 *   • label — the CTA copy inside the ready pill.
 */
export default function Msg91Widget({ onVerified, onError, onMobileTyped, label = 'Verify your mobile', sessionId }) {
  const [status, setStatus] = useState('loading-config'); // 'loading-config' | 'loading-sdk' | 'ready' | 'error' | 'fallback'
  const [message, setMessage] = useState('');
  const [uaClass] = useState(() => classifyUserAgent());
  const [showInAppHint, setShowInAppHint] = useState(() => isInAppBrowser(classifyUserAgent()));
  const initedRef = useRef(false);
  const firedRef = useRef(false); // guards against double-fire of MSG91 success callback
  const pollRef = useRef(null);   // interval id for shadow-DOM mobile polling
  const lastMobileRef = useRef(null);
  const onVerifiedRef = useRef(onVerified);
  const onErrorRef = useRef(onError);
  const onMobileTypedRef = useRef(onMobileTyped);
  onVerifiedRef.current = onVerified;
  onErrorRef.current = onError;
  onMobileTypedRef.current = onMobileTyped;

  // Iter-52.55.8 — best-effort funnel logging. Never blocks the flow.
  const logFunnel = (event_type, error) => {
    try {
      api.post('/analytics/signup-event', {
        session_id: sessionId || 'msg91-widget-anon',
        event_type,
        mode: 'phone_only',
        status: 'failed',
        error: (error || '').toString().slice(0, 280),
        user_agent_class: uaClass,
        build_version: BUILD_VERSION,
      }).catch(() => {});
    } catch { /* noop */ }
  };

  // Iter-52.31 — read the phone number the user typed into MSG91's
  // widget popup. The popup renders inside <msg91-otp-provider> as a
  // shadow-DOM custom element; the mobile input has id `init-contact`
  // and `#country-code-number-info` renders the FULL "+{dial}{digits}"
  // (E.164 form) as soon as the typed number is valid.
  //
  // Iter-52.33 — BUG FIX (was corrupting numbers on capture).
  //   Old code tried to extract just the "+dial" prefix out of
  //   `#country-code-number-info` using `/^\+(\d{1,4})/` and then
  //   append it to the raw `#init-contact` value. That greedy match
  //   swallowed 4 digits of the country-code-number-info text — so
  //   for a valid Indian number "+919212283404" the regex captured
  //   "+9192" as the dial code, then the raw input "9212283404" was
  //   appended, producing the corrupted "+91929212283404" the user
  //   reported. The dial was doubling as the first 2 digits of the
  //   mobile.
  //
  //   Fix: NEVER slice-and-recombine. Either:
  //     (a) trust `#country-code-number-info` wholesale when it looks
  //         like a complete E.164 (>=10 digits after +), OR
  //     (b) fall back to `#init-contact` and hardcode "+91" — we
  //         only sell to India, so any non-India selection is a
  //         mistake we should refuse to record.
  //   Then HARD-validate `/^\+91\d{10}$/` before returning. Anything
  //   else returns null so the parent caches nothing (a wrong number
  //   is strictly worse than no number — the Nudge feature would
  //   WhatsApp the wrong person).
  //
  // Returns a validated E.164 Indian mobile ("+919XXXXXXXXX") or null.
  const readWidgetMobile = () => {
    try {
      const host = document.querySelector('msg91-otp-provider');
      const root = host && host.shadowRoot;
      if (!root) return null;
      const input = root.getElementById('init-contact');
      const info = root.getElementById('country-code-number-info');
      return parseMsg91Mobile(
        input ? input.value : '',
        info ? info.textContent : '',
      );
    } catch { return null; }
  };

  useEffect(() => {
    let cancelled = false;

    async function boot() {
      try {
        // 1. Pull the widget config from the server. We don't hardcode
        //    widgetId/tokenAuth in the SPA bundle — they come from the
        //    backend's env-driven /widget-config endpoint so ops can
        //    rotate keys without a redeploy.
        const { data: cfg } = await api.get('/auth/msg91/widget-config');
        if (cancelled) return;
        if (!cfg?.widget_id || !cfg?.token_auth || !cfg?.configured) {
          setStatus('error');
          setMessage('Mobile verification is not configured. Please contact support.');
          onErrorRef.current?.('Mobile verification is not configured.');
          return;
        }
        setStatus('loading-sdk');

        // Start polling the shadow-DOM input every 400 ms so the
        // typed mobile is always cached — critical for capturing
        // drop-offs where MSG91's `failure` callback never fires
        // (e.g. user closes the popup or the whole tab). The parent
        // uses this cached value on `pagehide` to emit an "abandoned"
        // funnel event with the mobile still attached.
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = setInterval(() => {
          const m = readWidgetMobile();
          if (m && m !== lastMobileRef.current) {
            lastMobileRef.current = m;
            try { onMobileTypedRef.current?.(m); } catch { /* noop */ }
          }
        }, 400);

        // 2. Success/failure callbacks the widget will call. Named on
        //    `window` because the SDK invokes them from its own script
        //    context, not our React one.
        window.__readbuddyMsg91Success = (data) => {
          // Single-fire guard — see class docstring. MSG91's SDK CAN
          // fire this twice on rapid double-tap of Verify.
          if (firedRef.current) return;
          const token = data?.message;
          if (!token || typeof token !== 'string') {
            setStatus('error');
            setMessage('The verification service did not return a token.');
            onErrorRef.current?.('The verification service did not return a token.');
            return;
          }
          firedRef.current = true;
          // NOTE: we deliberately do NOT change `status` here. The
          // widget stays in 'ready' visually; the parent owns every
          // downstream message ("Creating your account…" / errors)
          // so there is exactly ONE source of truth on screen.
          // Iter-52.31 — pass the mobile alongside the token so the
          // parent can include it in funnel events.
          const mobile = readWidgetMobile();
          try { onVerifiedRef.current?.(token, mobile); } catch (e) { /* upstream will handle */ }
        };
        window.__readbuddyMsg91Failure = (err) => {
          const msg = err?.message || 'OTP verification cancelled.';
          // Reset the single-fire guard so the user can retry after
          // MSG91 tells us they failed / cancelled.
          firedRef.current = false;
          setStatus('error');
          setMessage(msg);
          // Iter-52.31 — capture the mobile at failure time too so
          // the funnel drilldown can show it for the Nudge button.
          const mobile = readWidgetMobile();
          onErrorRef.current?.(msg, mobile);
        };

        // 3. Guard against double-mount in React StrictMode: only
        //    load the script + call initSendOTP ONCE per page load.
        if (initedRef.current) {
          setStatus('ready');
          return;
        }
        initedRef.current = true;

        const configuration = {
          widgetId: cfg.widget_id,
          tokenAuth: cfg.token_auth,
          identifier: '',           // widget collects it
          exposeMethods: false,     // stay on default popup UI
          success: (d) => window.__readbuddyMsg91Success?.(d),
          failure: (e) => window.__readbuddyMsg91Failure?.(e),
        };

        if (typeof window.initSendOTP === 'function') {
          window.initSendOTP(configuration);
          setStatus('ready');
          return;
        }

        // Iter-52.55.12 — HOTFIX rollback of Iter-52.55.8 CORS regression.
        //
        // The Iter-52.55.8 loader set `s.crossOrigin='anonymous'` on the
        // <script> tag and cache-busted the URL. verify.msg91.com does
        // NOT send `Access-Control-Allow-Origin`, so the browser fetched
        // the script (HTTP 200) but REFUSED TO EXECUTE IT — which meant
        // `window.initSendOTP` never appeared, even on plain desktop
        // Chrome where the widget had worked for months. Symptom:
        // every signup on readbuddy.co.in failed with "Widget script
        // did not expose initSendOTP; falling back to manual OTP",
        // and the "manual OTP" endpoint (MSG91 widget REST API) also
        // returns "Invalid request" because that endpoint requires a
        // captcha hash only the widget's own JS can generate.
        //
        // The fix: load the script EXACTLY like we did pre-52.55.8 —
        // no `crossOrigin`, no cache-buster on the first attempt. We
        // keep the poll-for-initSendOTP loop and retry logic (those
        // don't hurt Chrome and help the in-app-browser edge case),
        // but retries only cache-bust the URL, not the first load.
        const SCRIPT_URL = 'https://verify.msg91.com/otp-provider.js';
        const inApp = isInAppBrowser(uaClass);
        const POLL_INTERVAL_MS = 250;
        const POLL_TIMEOUT_MS = inApp ? 4500 : 8000;
        const MAX_ATTEMPTS = inApp ? 2 : 3;

        const waitForInit = () => new Promise((resolve) => {
          const started = Date.now();
          const tick = () => {
            if (cancelled) return resolve(false);
            if (typeof window.initSendOTP === 'function') return resolve(true);
            if (Date.now() - started > POLL_TIMEOUT_MS) return resolve(false);
            setTimeout(tick, POLL_INTERVAL_MS);
          };
          tick();
        });

        const loadOnce = (attempt) => new Promise((resolve) => {
          const s = document.createElement('script');
          // First attempt: load the URL *exactly* like the browser
          // has cached it for months. Retries only: add a cache-buster
          // so a broken cached copy can't repeat.
          s.src = attempt > 0 ? `${SCRIPT_URL}?t=${Date.now()}` : SCRIPT_URL;
          s.type = 'text/javascript';
          s.async = true;
          // NOTE: DO NOT set s.crossOrigin — verify.msg91.com does not
          // send CORS headers and setting it blocks script execution.
          s.dataset.readbuddyMsg91Attempt = String(attempt);
          let done = false;
          s.onload = () => { if (!done) { done = true; resolve('load'); } };
          s.onerror = () => { if (!done) { done = true; resolve('error'); } };
          document.body.appendChild(s);
        });

        let ok = false;
        for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
          const outcome = await loadOnce(attempt);
          if (cancelled) return;
          if (outcome === 'load') {
            ok = await waitForInit();
            if (cancelled) return;
            if (ok) break;
          }
          // Small back-off before the next retry so a transient CDN
          // flake has a moment to recover.
          await new Promise(r => setTimeout(r, 600));
        }
        if (cancelled) return;
        if (ok && typeof window.initSendOTP === 'function') {
          window.initSendOTP(configuration);
          setStatus('ready');
          return;
        }
        // All retries exhausted — fall back to manual OTP so the
        // customer can still sign up. Log a widget_failed funnel event
        // (with UA class) so the admin sees how many leads hit this.
        // Iter-52.55.12 — do NOT surface the developer string
        // "Widget script did not expose initSendOTP…" to the parent's
        // red error banner. The technical detail already goes to the
        // funnel log; the parent just needs to know we handed off.
        logFunnel('widget_failed', 'initSendOTP not exposed after retries');
        setStatus('fallback');
        setMessage('Could not load the verification widget — using SMS OTP instead.');
      } catch (err) {
        if (cancelled) return;
        setStatus('error');
        setMessage(err?.response?.data?.detail || 'Could not start mobile verification.');
        onErrorRef.current?.(err?.message || 'Failed to load widget config.');
      }
    }

    boot();

    return () => {
      cancelled = true;
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      // Deliberately NOT removing the script — MSG91's widget attaches
      // its own DOM listeners and removing the script mid-flow can
      // leave stray handlers. It's safe to leave on the page; the
      // guard above prevents duplicate initialization.
      delete window.__readbuddyMsg91Success;
      delete window.__readbuddyMsg91Failure;
    };
    // logFunnel deliberately excluded — it reads live refs and would
    // re-trigger boot() on every render if listed as a dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="msg91-widget-shell" className="rounded-2xl border border-slate-200 bg-white/60 p-4 space-y-3">
      {showInAppHint && status !== 'fallback' && (
        <div data-testid="msg91-inapp-hint"
          className="rounded-xl border border-amber-300 bg-amber-50 p-2.5 flex items-start gap-2">
          <span className="text-lg shrink-0" aria-hidden="true">🌐</span>
          <div className="flex-1 min-w-0 space-y-1.5">
            <p className="text-[11px] md:text-xs font-black text-amber-900 leading-snug">
              Behtar anubhav ke liye {isIOS() ? 'Safari' : 'Chrome'} mein kholein. /
              बेहतर अनुभव के लिए {isIOS() ? 'Safari' : 'Chrome'} में खोलें।
            </p>
            {isIOS() ? (
              <p className="text-[10px] font-bold text-amber-800 leading-snug">
                Tap the "•••" or share icon at the bottom and choose <b>Open in Safari</b>.
              </p>
            ) : (
              <a href={openInChromeUrl()}
                data-testid="msg91-open-in-chrome"
                className="inline-flex items-center gap-1 text-[11px] font-black text-white bg-amber-600 hover:bg-amber-700 px-2.5 py-1 rounded-full">
                <ExternalLink size={11}/> Open in Chrome
              </a>
            )}
            <button type="button"
              onClick={() => setShowInAppHint(false)}
              className="ml-2 text-[10px] font-bold text-amber-800 underline">
              Continue here anyway
            </button>
          </div>
        </div>
      )}
      {status === 'loading-config' && (
        <div data-testid="msg91-widget-loading-config" className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <Loader2 size={16} className="animate-spin text-[#FF6B1A]"/>
          <span>OTP box load ho raha hai… / OTP बॉक्स लोड हो रहा है…</span>
        </div>
      )}
      {status === 'loading-sdk' && (
        <div data-testid="msg91-widget-loading-sdk" className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <Loader2 size={16} className="animate-spin text-[#FF6B1A]"/>
          <span>OTP box load ho raha hai… / OTP बॉक्स लोड हो रहा है…</span>
        </div>
      )}
      {status === 'ready' && (
        <div data-testid="msg91-widget-ready" className="flex items-center gap-2 text-sm font-bold text-[#2B2140]">
          <ShieldCheck size={16} className="text-[#FF6B1A]"/> {label} — MSG91 popup will open.
        </div>
      )}
      {status === 'error' && (
        <div data-testid="msg91-widget-error" className="flex items-start gap-2 text-sm text-red-600">
          <AlertCircle size={16} className="mt-0.5 flex-shrink-0"/><span>{message}</span>
        </div>
      )}
      {status === 'fallback' && (
        <Msg91ManualOtp
          sessionId={sessionId}
          onVerified={(token, mobile) => {
            firedRef.current = true;
            lastMobileRef.current = mobile;
            try { onVerifiedRef.current?.(token, mobile); } catch { /* upstream */ }
          }}
          onError={(msg) => { onErrorRef.current?.(msg); }}
        />
      )}
      <span data-testid="msg91-widget-api-base" hidden>{API}</span>
    </div>
  );
}
